<script>
import { useLandingPageStore } from "../../../stores/landingPage";
import { useAnalyticEventStore } from "../../../stores/analyticEvents";
import Icon from "../../components/Icon.vue";
import Popup from "../../components/popup.vue";
import CustomColorPicker from "../brand/colorPicker.vue";
import ColorCard from "../brand/colorCard.vue";
import ImageUploader from "../../components/imageUploader.vue";
import DragNDrop from "../brand/dragNDrop.vue";
import BaseModal from "../../components/BaseModal.vue";

export default {
  name: "BrandingTab",
  components: {
    DragNDrop,
    ImageUploader,
    ColorCard,
    CustomColorPicker,
    Popup,
    Icon,
    BaseModal
  },
  setup() {
    const landingStore = useLandingPageStore();
    const analyticEventStore = useAnalyticEventStore();
    return {
      landingStore,
      analyticEventStore
    };
  },
  data() {
    return {
      popupMessage: "",
      brandColors: {},
    };
  },
  computed: {
    brand() {
      return this.landingStore.localBrand;
    },
    resetButtonEnabled() {
      return this.landingStore.brandDirty;
    }
  },
  watch: {
    loading() {
      this.landingStore.dirtyDraft = false;
    },
    'landingStore.localBrand.color_primary'() {
      this.landingStore.updateColors();
    },
    'landingStore.localBrand.color_accent'() {
      this.landingStore.updateColors();
    },
    'landingStore.organizationId'() {
      this.landingStore.getBrand();
    }
  },
  methods: {
    resetBrand() {
      this.$emit('discard-branding');
      this.analyticEventStore.logEvent({
        action: `landing-builder-discard-branding-changes`,
        traceable_type: "Brand",
        traceable_id: this.landingStore.brand.layoutId,
        metadata: {
          discarded_changes: this.landingStore.localBrand,
        }
      });
      // Reset localBrand to the saved version
      this.landingStore.resetBrand();
    },
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    }
  },
};
</script>

<template>
  <div
    id="brand-tab"
    class="tab-content h-full overflow-y-auto"
  >
    <!-- Single Card Container for Everything -->
    <div class="bg-white rounded-xl shadow-lg p-6 mb-20">
      <!-- Header Section with Centered Buttons -->
      <div class="flex justify-end items-center border-b pb-4 mb-6">
        <div class="flex flex-row gap-4">
          <div
            class="icon-container enabled"
            :style="{ visibility: resetButtonEnabled ? 'visible' : 'hidden' }"
            @click="resetBrand"
          >
            <icon
              icon="trash"
              size="24px"
            />
            <div>Discard Changes</div>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="flex flex-col xl:flex-row space-y-5 xl:space-y-0 xl:space-x-5">
        <!-- Theme (Brand Colors) Section -->
        <div class="flex-1">
          <div class="text-lg font-semibold mb-5">
            Theme
          </div>
          <div class="text-base mb-5">
            Use the options below to customize your theme colors.
          </div>
          <!-- Responsive Flex Container for Color Picker Cards -->
          <div class="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <!-- Primary Colors Card -->
            <div class="border border-gray-200 px-4 pb-4 pt-2 rounded-lg flex-1">
              <span class="text-slate-600 text-sm font-semibold">
                Primary Colors
              </span>
              <custom-color-picker
                v-model="landingStore.localBrand.color_primary"
                :loading="landingStore.loading || landingStore.saving"
                class="flex flex-1 mt-2"
              />
              <div v-if="landingStore.localBrand.color_primary">
                <color-card
                  label="Primary"
                  :color="landingStore.localBrand.color_primary"
                  :pallet="landingStore.primaryPallet"
                  class="mt-4"
                  :loading="landingStore.loading || landingStore.saving"
                />
              </div>
            </div>
            <!-- Accent Colors Card -->
            <div class="border border-gray-200 px-4 pb-4 pt-2 rounded-lg flex-1">
              <span class="text-slate-600 text-sm font-semibold">
                Accent Colors
              </span>
              <custom-color-picker
                v-model="landingStore.localBrand.color_accent"
                :loading="landingStore.loading || landingStore.saving"
                class="flex flex-1 mt-2"
              />
              <div v-if="landingStore.localBrand.color_accent">
                <color-card
                  label="Accent"
                  :color="landingStore.localBrand.color_accent"
                  :pallet="landingStore.accentPallet"
                  class="mt-4"
                  :loading="landingStore.loading || landingStore.saving"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1" />
      </div>

      <!-- Popup Component -->
      <popup
        ref="popup"
        :message="popupMessage"
        :duration="1400"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

#brand-tab {
  .icon-container {
    cursor: not-allowed;
    @apply flex flex-row items-center;
    @apply rounded-md bg-default_secondary_dark px-2 pt-2 pb-1 mb-2;
    @apply bg-transparent text-sm font-semibold;
    div {
      @apply ml-2;
    }

    :deep(.icon) svg path {
      @apply fill-gray-500;
    }

    &.enabled {
      cursor: pointer;
      @apply bg-white;
      :deep(.icon) svg path {
        @apply fill-default_primary_dark;
      }
    }
  }
}

/* Override BaseModal styles so it’s contained within #brand-tab */
:deep(#base-modal-background) {
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  padding: 50px !important;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}
</style>
